import { createSlice } from '@reduxjs/toolkit';
import { mutateAgentSetting } from '../agentSettings/actions';
import { flowBuilderStreamEventReceived } from '../flowBuilder/actions';
import type { FlowBuilderStreamEventType } from '../../types/flowBuilder/flowBuilderStreamEvents';
import type { NegotiationAgent, NegotiationAgentsState } from '../../types/negotiationAgents';
import {
  fetchAgentAuxiliaryCandidates,
  fetchAgentLines,
  fetchAgentOffices,
  fetchAgentCatalog,
  fetchAgentResources,
  fetchNegotiationAgent,
  fetchNegotiationAgents,
  mutateNegotiationAgent,
  applyAgentRunningMigration,
  previewAgentRunningMigration,
  updateAgentAvailability,
  validateNegotiationAgent,
} from './actions';
import {
  agentAvailabilityMutationKey,
  agentCatalogQueryKey,
  agentQueryKey,
  markStaleByOwner,
  agentValidationKey,
} from '../../utils/negotiationAgents/queryKey';

/** Eventos do FlowBuilder que gravaram algo no rascunho do agente: catálogos e diagnóstico envelhecem. */
const FLOW_BUILDER_DRAFT_WRITES = new Set<FlowBuilderStreamEventType>([
  'draft_updated',
  'custom_field_created',
  'custom_function_created',
  'sub_flow_created',
  'flow_complete',
]);

/** Só sobe: um GET antigo que volte depois do evento não pode baixar a revisão do próximo turno. */
function advanceDraftRevision(agent: NegotiationAgent | undefined, draftRevision: number | null) {
  if (agent && draftRevision !== null && agent.draftRevision < draftRevision)
    agent.draftRevision = draftRevision;
}

function invalidateAgentDraftViews(state: NegotiationAgentsState, agentId: string) {
  markStaleByOwner(state.resources, JSON.stringify(['agentId', agentId]));
  markStaleByOwner(state.catalogs, JSON.stringify(['negotiationAgentId', agentId]));
  for (const key of Object.keys(state.validations))
    if (key === agentId || key.startsWith(`${agentId}:validation:`)) delete state.validations[key];
}

const initialState: NegotiationAgentsState = {
  agents: {},
  lists: {},
  resources: {},
  catalogs: {},
  offices: {},
  validations: {},
  mutations: {},
  detailStatus: {},
  lines: {},
  auxiliaryCandidates: {},
  runningMigrationPreviews: {},
  runningMigrationResults: {},
};
const slice = createSlice({
  name: 'negotiationAgents',
  initialState,
  reducers: {
    clearAgentMutation(state, action: { payload: string }) {
      delete state.mutations[action.payload];
    },
    clearAgentRunningMigration(state, action: { payload: string }) {
      delete state.runningMigrationPreviews[action.payload];
      delete state.runningMigrationResults[action.payload];
    },
  },
  extraReducers(builder) {
    // A revisão do rascunho que o editor manda no próximo turno (`expectedDraftRevision`) é esta.
    builder.addCase(flowBuilderStreamEventReceived, (state, action) => {
      const { agentId, event } = action.payload;
      const agent = state.agents[agentId];
      if (event.type === 'draft_updated') {
        advanceDraftRevision(agent, event.draftRevision);
        if (agent && event.principalFlowId) agent.principalFlowId = event.principalFlowId;
      }
      if (event.type === 'task_completed') advanceDraftRevision(agent, event.draftRevision);
      if (FLOW_BUILDER_DRAFT_WRITES.has(event.type)) invalidateAgentDraftViews(state, agentId);
    });
    builder.addCase(mutateAgentSetting.fulfilled, (state, action) => {
      const agent = state.agents[action.meta.arg.agentId];
      if (agent && agent.draftRevision < action.payload.draftRevision)
        agent.draftRevision = action.payload.draftRevision;
      const agentId = action.meta.arg.agentId;
      for (const key of Object.keys(state.validations))
        if (key === agentId || key.startsWith(`${agentId}:validation:`))
          delete state.validations[key];
    });
    builder.addCase(fetchNegotiationAgents.pending, (state, action) => {
      state.lists[agentQueryKey(action.meta.arg)] = {
        data: state.lists[agentQueryKey(action.meta.arg)]?.data ?? null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(fetchNegotiationAgents.fulfilled, (state, action) => {
      const entry = state.lists[action.payload.key];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = {
        ...action.payload.page,
        items: action.payload.page.items.map((agent) => agent.id),
      };
      entry.status = 'success';
      action.payload.page.items.forEach((agent) => {
        if (!state.agents[agent.id] || state.agents[agent.id].draftRevision <= agent.draftRevision)
          state.agents[agent.id] = agent;
      });
    });
    builder.addCase(fetchNegotiationAgents.rejected, (state, action) => {
      const entry = state.lists[agentQueryKey(action.meta.arg)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: action.error.message ?? 'Erro ao buscar agentes',
      };
    });
    builder.addCase(fetchAgentOffices.pending, (state, action) => {
      state.offices[agentQueryKey(action.meta.arg)] = {
        data: state.offices[agentQueryKey(action.meta.arg)]?.data ?? null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(fetchAgentOffices.fulfilled, (state, action) => {
      const entry = state.offices[action.payload.key];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload.page;
      entry.status = 'success';
    });
    builder.addCase(fetchAgentOffices.rejected, (state, action) => {
      const entry = state.offices[agentQueryKey(action.meta.arg)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Erro ao buscar escritórios',
      };
    });
    builder.addCase(fetchAgentCatalog.pending, (state, action) => {
      state.catalogs[agentCatalogQueryKey(action.meta.arg)] = {
        data: state.catalogs[agentCatalogQueryKey(action.meta.arg)]?.data ?? null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
        stale: false,
      };
    });
    builder.addCase(fetchAgentCatalog.fulfilled, (state, action) => {
      const entry = state.catalogs[action.payload.key];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload.page;
      entry.status = 'success';
    });
    builder.addCase(fetchAgentCatalog.rejected, (state, action) => {
      const entry = state.catalogs[agentCatalogQueryKey(action.meta.arg)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Erro ao buscar catálogo',
      };
    });
    builder.addCase(fetchAgentResources.pending, (state, action) => {
      state.resources[agentQueryKey(action.meta.arg)] = {
        data: state.resources[agentQueryKey(action.meta.arg)]?.data ?? null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
        stale: false,
      };
    });
    builder.addCase(fetchAgentResources.fulfilled, (state, action) => {
      const entry = state.resources[action.payload.key];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload.page;
      entry.status = 'success';
    });
    builder.addCase(fetchAgentResources.rejected, (state, action) => {
      const entry = state.resources[agentQueryKey(action.meta.arg)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Erro ao buscar recursos',
      };
    });
    builder.addCase(fetchNegotiationAgent.pending, (state, action) => {
      state.detailStatus[
        typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg.id
      ] = {
        data: null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(fetchNegotiationAgent.fulfilled, (state, action) => {
      const entry =
        state.detailStatus[
          typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg.id
        ];
      if (entry?.requestId !== action.meta.requestId) return;
      if (
        !state.agents[action.payload.id] ||
        state.agents[action.payload.id].draftRevision <= action.payload.draftRevision
      )
        state.agents[action.payload.id] = action.payload;
      entry.status = 'success';
    });
    builder.addCase(fetchNegotiationAgent.rejected, (state, action) => {
      const entry =
        state.detailStatus[
          typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg.id
        ];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? { code: 'REQUEST_FAILED', message: 'Erro ao buscar agente' };
    });
    builder.addCase(validateNegotiationAgent.pending, (state, action) => {
      state.validations[agentValidationKey(action.meta.arg)] = {
        data: null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(validateNegotiationAgent.fulfilled, (state, action) => {
      const entry = state.validations[agentValidationKey(action.meta.arg)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload;
      entry.status = 'success';
    });
    builder.addCase(validateNegotiationAgent.rejected, (state, action) => {
      const entry = state.validations[agentValidationKey(action.meta.arg)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? { code: 'REQUEST_FAILED', message: 'Erro ao validar agente' };
    });
    builder.addCase(mutateNegotiationAgent.pending, (state, action) => {
      state.mutations[action.meta.arg.key] = {
        data: null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(mutateNegotiationAgent.fulfilled, (state, action) => {
      const entry = state.mutations[action.payload.key];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload.result;
      entry.status = 'success';
      const result = action.payload.result;
      if (
        result &&
        'assistantName' in result &&
        (!state.agents[result.id] || state.agents[result.id].draftRevision <= result.draftRevision)
      )
        state.agents[result.id] = result;
      const agentId = action.meta.arg.agentId;
      if (agentId && action.meta.arg.operation !== 'copyPreview') {
        // Uma resposta iniciada antes da alteração não pode repovoar o catálogo antigo.
        const ownerKey = JSON.stringify(['agentId', agentId]);
        markStaleByOwner(state.resources, ownerKey);
        markStaleByOwner(state.auxiliaryCandidates, ownerKey);
        const catalogOwnerKey = JSON.stringify(['negotiationAgentId', agentId]);
        markStaleByOwner(state.catalogs, catalogOwnerKey);
        delete state.detailStatus[agentId];
      }
      // Sem a entidade, a lista descarta o ID na hora e o detalhe não reabre o editor de um
      // agente que o servidor já não devolve (voltar pelo histórico do navegador).
      if (agentId && !action.meta.arg.resource && action.meta.arg.operation === 'delete')
        delete state.agents[agentId];
      if (agentId && action.meta.arg.operation !== 'copyPreview')
        Object.keys(state.validations)
          .filter(
            (key) =>
              key === action.meta.arg.agentId ||
              key.startsWith(`${action.meta.arg.agentId}:validation:`),
          )
          .forEach((key) => {
            delete state.validations[key];
          });
    });
    builder.addCase(updateAgentAvailability.pending, (state, action) => {
      state.mutations[agentAvailabilityMutationKey(action.meta.arg.agentId)] = {
        data: null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(updateAgentAvailability.fulfilled, (state, action) => {
      const entry = state.mutations[agentAvailabilityMutationKey(action.meta.arg.agentId)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload;
      entry.status = 'success';
      // A disponibilidade não passa pelo rascunho: a revisão pode não mudar, por isso `<=`.
      const current = state.agents[action.payload.id];
      if (!current || current.draftRevision <= action.payload.draftRevision)
        state.agents[action.payload.id] = action.payload;
    });
    builder.addCase(updateAgentAvailability.rejected, (state, action) => {
      const entry = state.mutations[agentAvailabilityMutationKey(action.meta.arg.agentId)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Erro ao salvar a disponibilidade',
      };
    });
    builder.addCase(fetchAgentLines.pending, (state, action) => {
      state.lines[agentQueryKey(action.meta.arg)] = {
        data: state.lines[agentQueryKey(action.meta.arg)]?.data ?? null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(fetchAgentLines.fulfilled, (state, action) => {
      const entry = state.lines[action.payload.key];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload.page;
      entry.status = 'success';
    });
    builder.addCase(fetchAgentLines.rejected, (state, action) => {
      const entry = state.lines[agentQueryKey(action.meta.arg)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Erro ao buscar as linhas do agente',
      };
    });
    builder.addCase(fetchAgentAuxiliaryCandidates.pending, (state, action) => {
      const key = agentCatalogQueryKey(action.meta.arg);
      state.auxiliaryCandidates[key] = {
        data: state.auxiliaryCandidates[key]?.data ?? null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
        stale: false,
      };
    });
    builder.addCase(fetchAgentAuxiliaryCandidates.fulfilled, (state, action) => {
      const entry = state.auxiliaryCandidates[action.payload.key];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload.page;
      entry.status = 'success';
    });
    builder.addCase(fetchAgentAuxiliaryCandidates.rejected, (state, action) => {
      const entry = state.auxiliaryCandidates[agentCatalogQueryKey(action.meta.arg)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Erro ao buscar os recursos da conta do provedor',
      };
    });
    builder.addCase(previewAgentRunningMigration.pending, (state, action) => {
      state.runningMigrationPreviews[action.meta.arg] = {
        data: null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(previewAgentRunningMigration.fulfilled, (state, action) => {
      const entry = state.runningMigrationPreviews[action.meta.arg];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload;
      entry.status = 'success';
    });
    builder.addCase(previewAgentRunningMigration.rejected, (state, action) => {
      const entry = state.runningMigrationPreviews[action.meta.arg];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Erro ao calcular a prévia da migração',
      };
    });
    builder.addCase(applyAgentRunningMigration.pending, (state, action) => {
      state.runningMigrationResults[action.meta.arg.agentId] = {
        data: null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(applyAgentRunningMigration.fulfilled, (state, action) => {
      const entry = state.runningMigrationResults[action.meta.arg.agentId];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload;
      entry.status = 'success';
    });
    builder.addCase(applyAgentRunningMigration.rejected, (state, action) => {
      const entry = state.runningMigrationResults[action.meta.arg.agentId];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Erro ao aplicar a migração',
      };
    });
    builder.addCase(mutateNegotiationAgent.rejected, (state, action) => {
      const entry = state.mutations[action.meta.arg.key];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? { code: 'REQUEST_FAILED', message: 'Erro ao salvar' };
    });
  },
});
export const { clearAgentMutation, clearAgentRunningMigration } = slice.actions;
export default slice.reducer;
